/**
 * useConnectorTypes - Hook for fetching connector type metadata from API
 *
 * Provides React Query-powered data fetching for connector types with caching
 */
import { useQuery } from '@tanstack/react-query';
import { fetchConnectorTypes, fetchConnectorTypeDetails, fetchConnectorTypeSchema } from '@/lib/api/connector-types';
import type { ConnectorType } from '@/types/connectors';
// Re-export for consumers that need the full schema type alongside the hook
export type { ConnectorTypeSchemaResponse } from '../types/connector-schema';

/**
 * Hook to fetch all connector types with optional filtering
 *
 * @param params - Optional filtering parameters
 * @param params.category - Filter by category (advertising, database, etc.)
 * @param params.available_only - Only return available connectors
 * @param params.include_planned - Include planned/upcoming connectors
 * @returns React Query result with connector types data
 */
export function useConnectorTypes(params?: {
  category?: string;
  available_only?: boolean;
  include_planned?: boolean;
}) {
  return useQuery({
    queryKey: ['connectorTypes', params],
    queryFn: () => fetchConnectorTypes(params),
    staleTime: 15 * 60 * 1000, // 15 minutes - connector types rarely change
    gcTime: 30 * 60 * 1000, // 30 minutes cache time
    retry: 2, // Retry failed requests up to 2 times
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });
}

/**
 * Hook to fetch specific connector type details
 *
 * @param type - Connector type to fetch
 * @param enabled - Whether to enable the query (default: true)
 * @returns React Query result with connector type details
 */
export function useConnectorTypeDetails(type: ConnectorType | null, enabled: boolean = true) {
  return useQuery({
    queryKey: ['connectorType', type],
    queryFn: () => fetchConnectorTypeDetails(type!),
    enabled: enabled && !!type,
    staleTime: 15 * 60 * 1000, // 15 minutes
    gcTime: 30 * 60 * 1000,
  });
}

/**
 * Hook to fetch field schema for a connector type
 *
 * @param type - Connector type to fetch schema for
 * @param enabled - Whether to enable the query (default: true)
 * @returns React Query result with connector type schema
 */
export function useConnectorTypeSchema(type: ConnectorType | null, enabled: boolean = true) {
  return useQuery({
    queryKey: ['connectorTypeSchema', type],
    queryFn: () => fetchConnectorTypeSchema(type!),
    enabled: enabled && !!type,
    staleTime: 15 * 60 * 1000, // 15 minutes
    gcTime: 30 * 60 * 1000,
  });
}
